import { useNavigate } from 'react-router-dom'

import { useApolloClient, useQuery } from '@apollo/client'
import {
  CalendarEditLeavePageDocument,
  CalendarPageQueryDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { SkeletonModal } from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'

import { useCalendarLeaves } from 'modules/calendar/hooks/useCalendarLeaves'
import EditLeaveModal from 'modules/timeoff/components/EditLeaveModal'
import LeaveRecommendationModal from 'modules/timeoff/components/LeaveRecommendationModal'
import { useLeaveRecommendationFeatureFlag } from 'modules/timeoff/hooks/useLeaveRecommendationFeatureFlag'
import { useOnCloseCalendar } from 'modules/timeoff/hooks/useOnCloseCalendar'

const CalendarEditLeavePage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { leave_id: leaveId, year } = useStrictParams<'leave_id' | 'year'>()

  const onReject = () => {
    if (leaveId) {
      navigate(navigation.calendar.leaves.rejectLeave.fullPath({ leave_id: leaveId }))
    }
  }

  const apolloClient = useApolloClient()
  const onClose = useOnCloseCalendar()
  const { onChangeLeave } = useCalendarLeaves()

  const { loading: ffLoading, enabled: isRecommendationEnabled } =
    useLeaveRecommendationFeatureFlag()

  const { loading: pageLoading, data } = useQuery(CalendarEditLeavePageDocument, {
    variables: {
      leaveId: String(leaveId),
    },
  })

  const handleClose = () => {
    apolloClient.refetchQueries({
      include: [CalendarPageQueryDocument],
    })
    onChangeLeave()
    onClose()
  }

  if (ffLoading || pageLoading) {
    return <SkeletonModal />
  }

  if (!data?.apiCore.currentsConnection.edges[0]?.node.employee) {
    throw new Error('Current employee not found')
  }

  const currentEmployee = data.apiCore.currentsConnection.edges[0].node.employee

  const customResourceFieldsData =
    compact(
      data?.documents.foldersConnection.edges[0]?.node?.resourceFieldsConnection.edges.map(
        (edge) => edge.node
      )
    ) ?? []

  const leave = data?.timeoff.leave

  if (!leave) {
    throw new Error('Leave not found')
  }

  const leaveEmployee = leave.employee

  if (!leaveEmployee) {
    throw new Error('Leave employee not found')
  }

  const sickFolderId = data.documents.foldersConnection.edges[0]?.node?.id

  if (!sickFolderId) {
    throw new Error('Sick folder not found')
  }

  return (
    <>
      {isRecommendationEnabled ? (
        <LeaveRecommendationModal
          currentEmployee={currentEmployee}
          customResourceFieldsData={customResourceFieldsData}
          delegatedApprover={leave.delegatedApproverAccess}
          fromCalendar
          leaveEmployee={leaveEmployee}
          leaveId={String(leaveId)}
          leaveYear={Number(year)}
          onChange={handleClose}
          onClose={handleClose}
          onReject={onReject}
          sickFolderId={sickFolderId}
          leaveCanManageLeaveDocuments={leave._policies?.manageLeaveDocuments?.can ?? false}
          recommendation={leave.approvalRecommendation}
        />
      ) : (
        <EditLeaveModal
          currentEmployee={currentEmployee}
          customResourceFieldsData={customResourceFieldsData}
          delegatedApprover={leave.delegatedApproverAccess}
          fromCalendar
          leaveEmployee={leaveEmployee}
          leaveId={String(leaveId)}
          leaveYear={Number(year)}
          onChange={onChangeLeave}
          onClose={onClose}
          onReject={onReject}
          sickFolderId={sickFolderId}
          leaveCanManageLeaveDocuments={leave._policies?.manageLeaveDocuments?.can ?? false}
        />
      )}
    </>
  )
}

export default CalendarEditLeavePage
